import React, { useRef, useState } from 'react'
import { API } from 'aws-amplify'

interface ImageUploadProps {
  id: string
  onImageUploaded: (imageUrl: string | null) => void
  onError: (error: string) => void
}

interface PresignedUrlResponse {
  upload_url: string
  image_url: string
  key: string
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

const ImageUpload = ({ id, onImageUploaded, onError }: ImageUploadProps): JSX.Element => {
  const [preview, setPreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      onError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.')
      return
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      onError('File is too large. Maximum size is 10MB.')
      return
    }

    // Show preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreview(reader.result as string)
    }
    reader.readAsDataURL(file)
    setFileName(file.name)

    // Upload the file
    await uploadFile(file)
  }

  const uploadFile = async (file: File): Promise<void> => {
    setIsUploading(true)
    try {
      // Get presigned URL from backend
      const presignedResponse: PresignedUrlResponse = await API.post('MasjidNotifierAPI', '/presigned-url', {
        body: {
          content_type: file.type
        },
        headers: {
          'Content-Type': 'application/json'
        }
      })

      // Upload directly to S3 using presigned URL
      const uploadResponse = await fetch(presignedResponse.upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to S3')
      }

      // Return the final image URL
      onImageUploaded(presignedResponse.image_url)
    } catch (error) {
      console.error('Upload error:', error)
      onError('Failed to upload image. Please try again.')
      onImageUploaded(null)
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemove = (): void => {
    setPreview(null)
    setFileName(null)
    onImageUploaded(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = (): void => {
    fileInputRef.current?.click()
  }

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        id={id}
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {!preview ? (
        <button
          type="button"
          onClick={handleClick}
          disabled={isUploading}
          className="w-full p-4 text-sm rounded-lg border border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 focus:ring-2 focus:outline-none focus:ring-teal-500 cursor-pointer"
        >
          <div className="flex flex-col items-center justify-center">
            <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-gray-500">
              {isUploading ? 'Uploading...' : 'Click to upload image (optional)'}
            </span>
            <span className="text-xs text-gray-400 mt-1">JPEG, PNG, GIF, WebP up to 10MB</span>
          </div>
        </button>
      ) : (
        <div className="relative w-full rounded-lg border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={handleClick}
            disabled={isUploading}
            className="w-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <img
              src={preview}
              alt="Preview"
              className="w-full h-48 object-cover"
            />
            <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
              Click to change
            </div>
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 flex justify-between items-center">
            <span className="text-sm truncate">{fileName}</span>
            <button
              type="button"
              onClick={handleRemove}
              disabled={isUploading}
              className="text-white hover:text-red-300 ml-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {isUploading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
              <svg className="animate-spin h-8 w-8 text-teal-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ImageUpload
